import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePlanContext, planSdkKeys } from '@onxite/plan-sdk';
import { useT } from '@/lib/i18n/useT';
import { queryKeys } from '@/lib/api/queryKeys';

/**
 * Post-Stripe landing page. Stripe redirects here with
 * `?session_id={CHECKOUT_SESSION_ID}` after a successful checkout. We poll
 * `/plans/checkout/confirm/:sessionId` for up to 30s waiting for the webhook
 * to materialise the EcInvoicePlan row, then invalidate the current-plan
 * cache so every consumer of the SDK picks up the new caps.
 *
 * Aterriza en `/settings/billing`, que es la página de Facturación real: la
 * antigua `/billing` no existía y el catch-all mandaba al inicio sin rastro
 * del cambio de plan.
 */
const BILLING_PATH = '/settings/billing';
export function BillingSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { api } = usePlanContext();
  const t = useT();
  const startedAt = useRef(Date.now());
  const [status, setStatus] = useState<'pending' | 'ready' | 'timeout'>(
    'pending',
  );

  useEffect(() => {
    const sessionId = params.get('session_id');
    if (!sessionId) {
      navigate(BILLING_PATH, { replace: true });
      return;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.confirmCheckout(sessionId);
        if (cancelled) return;
        if (res.status === 'ready') {
          // El plan del SDK y, además, lo que pinta Facturación: plan
          // actual, facturas y catálogo, para que la página ya llegue nueva.
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: planSdkKeys.current() }),
            queryClient.invalidateQueries({ queryKey: queryKeys.plans.all }),
          ]);
          setStatus('ready');
          setTimeout(() => navigate(BILLING_PATH, { replace: true }), 1200);
          return;
        }
        if (Date.now() - startedAt.current > 30_000) {
          setStatus('timeout');
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled && Date.now() - startedAt.current < 30_000) {
          setTimeout(poll, 2000);
        } else {
          setStatus('timeout');
        }
      }
    };
    void poll();

    return () => {
      cancelled = true;
    };
  }, [params, navigate, api, queryClient]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6">
      {status === 'pending' && (
        <>
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-[var(--color-border-default)] border-t-[var(--color-accent)]" />
          <p className="text-sm text-[var(--color-fg-muted)]">{t('billingSuccess.confirming')}</p>
        </>
      )}
      {status === 'ready' && (
        <>
          <h2 className="text-2xl font-bold">{t('billingSuccess.readyTitle')}</h2>
          <p className="text-sm text-[var(--color-fg-muted)]">{t('billingSuccess.readyBody')}</p>
        </>
      )}
      {status === 'timeout' && (
        <>
          <h2 className="text-2xl font-bold">{t('billingSuccess.timeoutTitle')}</h2>
          <p className="max-w-md text-center text-sm text-[var(--color-fg-muted)]">{t('billingSuccess.timeoutBody')}</p>
          <button
            onClick={() => navigate(BILLING_PATH, { replace: true })}
            className="rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-[var(--color-fg-on-accent)]"
          >
            {t('billingSuccess.goToBilling')}
          </button>
        </>
      )}
    </div>
  );
}
